import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Copy, CalendarIcon, User, Clock } from "lucide-react";
import { format, addDays } from "date-fns";
import { cn, formatTime, getColorById } from "../lib/utils";
import { BACKEND_URL } from "../lib/api";

const CopySingleShiftDialog = ({
  open,
  onOpenChange,
  shift,
  sourceEmployee,
  employees = [],
  onCopy,
}) => {
  const [targetEmployeeId, setTargetEmployeeId] = useState("");
  const [targetDate, setTargetDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open && sourceEmployee) {
      setTargetEmployeeId(sourceEmployee.id);
      // Default to tomorrow
      setTargetDate(addDays(new Date(), 1));
      setLoading(false);
    }
  }, [open, sourceEmployee]);

  const handleCopy = async () => {
    if (!targetEmployeeId || !targetDate) return;
    
    setLoading(true);
    try {
      await onCopy({
        shift,
        targetEmployeeId,
        targetDate: format(targetDate, "yyyy-MM-dd"),
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to copy shift:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!shift || !sourceEmployee) return null;

  const sourceColor = getColorById(sourceEmployee.color);
  const targetEmployee = employees.find(e => e.id === targetEmployeeId);
  const targetColor = targetEmployee ? getColorById(targetEmployee.color) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="copy-shift-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5" />
            Copy Shift
          </DialogTitle>
          <DialogDescription>
            Copy this shift to another employee or date
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Source Shift Info */}
          <div className="p-3 rounded-lg bg-muted/50 space-y-2">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
              Copying From
            </p>
            <div className="flex items-center gap-3">
              <div 
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0",
                  sourceColor.bg,
                  sourceColor.text
                )}
              >
                {sourceEmployee.avatar_url ? (
                  <img 
                    src={sourceEmployee.avatar_url.startsWith('http') ? sourceEmployee.avatar_url : `${BACKEND_URL}${sourceEmployee.avatar_url}`}
                    alt={sourceEmployee.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  sourceEmployee.name?.charAt(0)?.toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{sourceEmployee.name}</p>
                <p className="text-sm text-muted-foreground tabular-nums">
                  {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                </p>
              </div>
            </div>
          </div>

          {/* Target Employee */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Copy To Employee
            </Label>
            <Select value={targetEmployeeId} onValueChange={setTargetEmployeeId}>
              <SelectTrigger data-testid="copy-shift-employee-select">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => {
                  const empColor = getColorById(emp.color);
                  return (
                    <SelectItem key={emp.id} value={emp.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className={cn(
                            "w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold",
                            empColor.bg,
                            empColor.text
                          )}
                        >
                          {emp.avatar_url ? (
                            <img 
                              src={emp.avatar_url.startsWith('http') ? emp.avatar_url : `${BACKEND_URL}${emp.avatar_url}`}
                              alt={emp.name}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                          ) : (
                            emp.name?.charAt(0)?.toUpperCase()
                          )}
                        </div>
                        <span>{emp.name}</span>
                        {emp.id === sourceEmployee.id && (
                          <span className="text-xs text-muted-foreground">(same)</span>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Target Date */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Copy To Date
            </Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !targetDate && "text-muted-foreground"
                  )}
                  data-testid="copy-shift-date-picker"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {targetDate ? format(targetDate, "EEE, MMM d, yyyy") : "Select date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={targetDate}
                  onSelect={(date) => {
                    setTargetDate(date);
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Preview */}
          {targetEmployee && targetDate && (
            <div className="p-3 rounded-lg border border-dashed space-y-2">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                Preview
              </p>
              <div className="flex items-center gap-3">
                <div 
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0",
                    targetColor?.bg,
                    targetColor?.text
                  )}
                >
                  {targetEmployee.avatar_url ? (
                    <img 
                      src={targetEmployee.avatar_url.startsWith('http') ? targetEmployee.avatar_url : `${BACKEND_URL}${targetEmployee.avatar_url}`}
                      alt={targetEmployee.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    targetEmployee.name?.charAt(0)?.toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{targetEmployee.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {format(targetDate, "EEE, MMM d")} • {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleCopy} 
            disabled={!targetEmployeeId || !targetDate || loading}
            data-testid="copy-shift-confirm-btn"
          >
            {loading ? "Copying..." : "Copy Shift"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopySingleShiftDialog;
